import React from 'react';
import { useSelector } from 'react-redux';

import _get from 'lodash/get';
import _some from 'lodash/some';

import InfoIcon from 'cccisd-icons/info2';
import Tooltip from 'cccisd-tooltip';

import { toLocalDate } from '../../helpers';

import style from './style.css';

const isSupervisor = window.cccisd?.fortress?.user?.acting?.role?.handle === 'supervisor';

const ResponseRateBox = ({ label }) => {
    const role = label.toLowerCase();

    const backgroundColor = role === 'caregiver' ? '#e7a32a' : '#806fa6';

    const aggregateScoreAverages = useSelector(state => state.app.dashboard.aggregateScoreAverages);
    const allSessions = useSelector(state => state.app.dashboard.youthAndCaregiverSessions);
    const filteredYouthList = useSelector(state => (isSupervisor ? state.app.supervisor.filteredYouthList : null));
    const selectedYouth = useSelector(state => state.app.dashboard.selectedYouth);

    const youthAndCaregiverSessions = (() => {
        if (!isSupervisor || !filteredYouthList) return allSessions;
        const validPawnIds = new Set([
            ...filteredYouthList.map(y => y.pawn.pawnId),
            ...filteredYouthList.flatMap(y => (y.parentRoles?.caregiverList || []).map(c => c.pawn.pawnId)),
        ]);
        return allSessions.filter(s => validPawnIds.has(s.pawnId));
    })();
    const selectedYouthPlanDeploymentIds = selectedYouth
        ? selectedYouth.selectedAssignmentPlanList.map(assignmentPlan => assignmentPlan.assignmentPlanId)
        : [];

    // filters
    const startDateFilterValue = useSelector(state => state.app.dashboard.filters.startDate);
    const endDateFilterValue = useSelector(state => state.app.dashboard.filters.endDate);
    const planNameFilterValue = useSelector(state => state.app.dashboard.filters.planName);

    const roleSessions = youthAndCaregiverSessions
        .filter(session => {
            // if a youth is selected instead of 'All':
            // only include the session if the session is for the selected youth
            // OR, if it is for a caregiver of the selected youth AND
            // the session of that caregiver is part of the parent deployment plans of the selected youth
            // (youths can share caregivers, so a caregiver that is part of the selected youth
            // can have session data not relevant for the selected youth)
            const appliesToSelectedUser =
                !selectedYouth ||
                (session.role === role && session.pawnId === selectedYouth.pawn.pawnId) ||
                (session.role === role &&
                    selectedYouth.parentRoles.caregiverList.find(
                        caregiver =>
                            caregiver.pawn.pawnId === session.pawnId &&
                            _some(selectedYouthPlanDeploymentIds, id => id === session.planDeploymentId)
                    ));

            return session.role === role && appliesToSelectedUser;
        })
        .filter(entry => !planNameFilterValue || entry.planName.toLowerCase() === planNameFilterValue.toLowerCase());

    const filterSessionsByDate = (sessions, dateToFilter) => {
        return sessions
            .filter(
                session =>
                    !endDateFilterValue || toLocalDate(_get(session, dateToFilter, '')) <= new Date(endDateFilterValue)
            )
            .filter(
                session =>
                    !startDateFilterValue ||
                    toLocalDate(_get(session, dateToFilter, '')) >= new Date(startDateFilterValue)
            );
    };

    const sentSessions = roleSessions.map(entry => ({
        ...entry,
        sessions: entry.sessions.filter(session => _get(session, 'settings.messageScheduled', false)),
    }));

    const sessionsFlat = sentSessions.map(entry => entry.sessions).flat();
    const totalSent = sessionsFlat.length;

    const totalCompleted = filterSessionsByDate(
        sessionsFlat.filter(session => session.completed),
        'completedAt'
    ).length;

    const totalStarted = filterSessionsByDate(
        sessionsFlat.filter(session => session.startedDate),
        'startedDate'
    ).length;

    const completionPercentage = totalSent === 0 ? 0 : Math.round((totalCompleted / totalSent) * 100);
    const startedPercentage = totalSent === 0 ? 0 : Math.round((totalStarted / totalSent) * 100);

    const respondentCount = aggregateScoreAverages
        ? Object.keys(
              Object.values(aggregateScoreAverages).reduce((cnt, timepointData) => {
                  Object.values(timepointData).forEach(domainData => {
                      Object.values(domainData).forEach(pawnData => {
                          Object.entries(pawnData).forEach(([pawnId, entry]) => {
                              if (entry.role === role) cnt[pawnId] = true;
                          });
                      });
                  });
                  return cnt;
              }, {})
          ).length
        : null;

    return (
        <div className={style.response_rate_box} style={{ backgroundColor }}>
            <div className={style.response_rate_role}>
                {label}
                {respondentCount !== null && <span className={style.response_rate_n}>{`n=${respondentCount}`}</span>}
            </div>
            <div className={style.dividing_line} />
            <div className={style.response_rate_stats}>
                <div className={style.response_rate_stat}>
                    <div className={style.response_rate_stat_label}>
                        Completion Rate{' '}
                        <Tooltip title="Accounts for surveys that have been fully completed." placement="top">
                            <InfoIcon />
                        </Tooltip>
                    </div>
                    <div className={style.response_rate_percentage}>{completionPercentage}%</div>
                    <div className={style.response_rate_count}>
                        {totalCompleted}/{totalSent} sent
                    </div>
                </div>
                <div className={style.response_rate_divider} />
                <div className={style.response_rate_stat}>
                    <div className={style.response_rate_stat_label}>
                        Start Rate{' '}
                        <Tooltip
                            title="Accounts for surveys that have only been opened or partially completed."
                            placement="top"
                        >
                            <InfoIcon />
                        </Tooltip>
                    </div>
                    <div className={style.response_rate_percentage}>{startedPercentage}%</div>
                    <div className={style.response_rate_count}>
                        {totalStarted}/{totalSent} sent
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResponseRateBox;
